import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { EntityKeyByReference, GlobalEntityDefinition } from '@card-software/library';
import { BaseTransactionStrategy } from './base-transaction.strategy';
import { SupplyWarehouseDocumentType } from '../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TableColumn } from '../../models/supply-warehouse/table-column.model';
import { TransactionAction } from '../../models/supply-warehouse/transaction-action.model';
import { GoodsReceiptEntry, GoodsReceiptStatus } from '../../models/inventory/goods-receipt';
import { PurchaseOrderReceivedQuantity } from '../../models/inventory/purchase-order-received-quantity';
import { PurchaseOrder } from '../../models/order/purchase-order/purchase-order';
import { GoodsReceiptProxyService } from '../../proxies/inventory/goods-receipt.proxy.service';
import { PurchaseOrderProxyService } from '../../proxies/order/purchase-order.proxy.service';

/**
 * Estrategia para manejar Goods Receipt Notes (GRN)
 * Documenta la recepción de mercancía desde Purchase Orders
 */
@Injectable({
  providedIn: 'root',
})
export class GRNStrategy extends BaseTransactionStrategy<GoodsReceiptEntry, GoodsReceiptStatus> {
  readonly documentType = SupplyWarehouseDocumentType.GoodsReceiptNote;
  readonly initiatorName = 'Purchase Order';
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  readonly locationAssignmentType: 'storage' = 'storage';

  constructor(
    private goodsReceiptService: GoodsReceiptProxyService,
    private purchaseOrderService: PurchaseOrderProxyService,
  ) {
    super();
  }

  // ==================== Métodos de Carga ====================

  loadDocument(id: string): Observable<GoodsReceiptEntry | null> {
    return this.goodsReceiptService.getById(id).pipe(map((response) => response.data || null));
  }

  loadInitiatorDocument(purchaseOrderId: string): Observable<PurchaseOrder | null> {
    return this.purchaseOrderService.getById(purchaseOrderId).pipe(map((response) => response.data || null));
  }

  searchTransactionDocuments(purchaseOrderId: string): Observable<GoodsReceiptEntry[]> {
    const entityKey = this.getInitiatorEntityKey(purchaseOrderId);
    return this.goodsReceiptService.search({ initiator: entityKey }).pipe(map((response) => response.data || []));
  }

  override loadNetProcessedQuantities(purchaseOrderId: string): Observable<PurchaseOrderReceivedQuantity[] | null> {
    return this.goodsReceiptService.getReceivedQuantitiesByPurchaseOrder(purchaseOrderId).pipe(map((response) => response.data || []));
  }

  createFromInitiator(purchaseOrderId: string, locationCode: string): Observable<GoodsReceiptEntry> {
    const entityKey = this.getInitiatorEntityKey(purchaseOrderId);

    // Crear nuevo GRN desde Purchase Order con locationCode
    return this.goodsReceiptService.createFromEntityKey(entityKey, locationCode).pipe(map((response) => response.data!));
  }

  // ==================== Métodos de Actualización ====================

  updateDocument(id: string, document: GoodsReceiptEntry): Observable<GoodsReceiptEntry> {
    return this.goodsReceiptService.update(id, document).pipe(map((response) => response.data!));
  }

  updateStatus(id: string, newStatus: GoodsReceiptStatus): Observable<GoodsReceiptEntry> {
    // Usar el método específico de updateStatus
    return this.goodsReceiptService.updateStatus(id, newStatus).pipe(map((response) => response.data!));
  }

  // ==================== Configuración de UI ====================

  getTableColumns(): TableColumn[] {
    return [
      {
        field: 'itemCode',
        header: 'Item Code',
        width: '150px',
        type: 'text',
        sortable: true,
      },
      { field: 'itemDescription', header: 'Item Description', width: '250px', type: 'text' },
      {
        field: 'quantity',
        header: 'GRN Quantity',
        width: '120px',
        type: 'numeric',
        editable: true,
      },
      {
        field: 'uom',
        header: 'UOM',
        width: '100px',
        type: 'text',
      },
      {
        field: 'unitPrice',
        header: 'Unit Price',
        width: '130px',
        type: 'numeric',
      },
      {
        field: 'totalPrice',
        header: 'Total Price',
        width: '140px',
        type: 'numeric',
      },
      {
        field: 'storageLocations',
        header: 'Storage Locations',
        type: 'locations',
        editable: true,
      },
      {
        field: 'comments',
        header: 'GRN Comments',
        width: '200px',
        type: 'text',
        editable: true,
      },
      {
        field: 'actions',
        header: 'Common Actions',
        width: '100px',
        type: 'actions',
      },
    ];
  }

  getInitiatorItemsColumns(): TableColumn[] {
    return [
      {
        field: 'itemCode',
        header: 'Item Code',
        width: '150px',
        sortable: true,
      },
      {
        field: 'itemDescription',
        header: 'Item Description',
        width: '250px',
      },
      {
        field: 'ownerRequisition',
        header: 'Owner Requisition',
        width: '150px',
      },
      {
        field: 'quantity',
        header: 'Ordered Qty',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'uom',
        header: 'UOM',
        width: '100px',
        type: 'text',
      },
      {
        field: 'unitPrice',
        header: 'Unit Price',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'totalPrice',
        header: 'Total Price',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'receivedQuantity',
        header: 'Received Qty',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'pendingQuantity',
        header: 'Pending Qty',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'actions',
        header: 'Common Actions',
        width: '100px',
        type: 'actions',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAvailableActions(status: GoodsReceiptStatus, document: GoodsReceiptEntry): TransactionAction[] {
    const actions: TransactionAction[] = [];

    switch (status) {
      case GoodsReceiptStatus.Pending:
        actions.push({
          id: 'start-processing',
          label: 'Start Processing',
          icon: 'pi pi-play',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to start processing this GRN?',
        });
        actions.push({
          id: 'cancel',
          label: 'Cancel',
          icon: 'pi pi-times',
          severity: 'danger',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to cancel this GRN?',
        });
        break;

      case GoodsReceiptStatus.Processing:
        actions.push({
          id: 'complete',
          label: 'Complete',
          icon: 'pi pi-check',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to complete this GRN? This will create an inventory transaction.',
        });
        // actions.push({
        //   id: 'back-to-pending',
        //   label: 'Back to Pending',
        //   icon: 'pi pi-refresh',
        //   severity: 'warn',
        // });
        break;

      case GoodsReceiptStatus.Completed:
        // Solo visualización, no acciones de edición
        break;

      case GoodsReceiptStatus.Cancelled:
        actions.push({
          id: 'reopen',
          label: 'reopen',
          icon: 'pi pi-replay',
          severity: 'warn',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to reopen this GRN?',
        });
        break;
    }

    return actions;
  }

  // ==================== Reglas de Negocio ====================

  canEditQuantities(status: GoodsReceiptStatus): boolean {
    return status === GoodsReceiptStatus.Pending || status === GoodsReceiptStatus.Processing;
  }

  canEditLines(status: GoodsReceiptStatus): boolean {
    return status === GoodsReceiptStatus.Pending || status === GoodsReceiptStatus.Processing;
  }

  validateForStatusChange(document: GoodsReceiptEntry, newStatus: GoodsReceiptStatus): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validar que tenga líneas
    if (!document.lines || document.lines.length === 0) {
      errors.push('GRN must have at least one line');
    }

    // Validar que todas las líneas tengan cantidad > 0
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.lines?.forEach((line: any, index: number) => {
      if (line.quantity <= 0) {
        errors.push(`GRN_Error_Invalid_Quantity_Line_${index + 1}`);
      }
    });

    // Si cambia a Processing o Completed, validar ubicaciones
    if (newStatus === GoodsReceiptStatus.Processing || newStatus === GoodsReceiptStatus.Completed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.lines?.forEach((line: any, index: number) => {
        if (!line.storageLocations || line.storageLocations.length === 0) {
          errors.push(`GRN_Error_No_Locations_Line_${index + 1}`);
        } else {
          // Validar que la suma de ubicaciones = cantidad
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const totalInLocations = line.storageLocations.reduce((sum: number, loc: any) => sum + loc.quantity, 0);
          const diff = Math.abs(totalInLocations - line.quantity);
          if (diff > 0.001) {
            // Tolerancia para decimales
            errors.push(`GRN_Error_Location_Mismatch_Line_${index + 1}`);
          }
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  // ==================== Helpers ====================

  getDocumentTitle(document: GoodsReceiptEntry | null): string {
    if (!document) return 'New Goods Receipt Note';
    return document.number || 'Goods Receipt Note';
  }

  getInitiatorEntityKey(purchaseOrderId: string): EntityKeyByReference {
    return {
      reference: purchaseOrderId,
      entityDefinitionCode: GlobalEntityDefinition.PurchaseOrder,
    };
  }

  // ==================== Report Download ====================

  override supportsReportDownload(): boolean {
    return true;
  }

  override canDownloadReport(status: GoodsReceiptStatus): boolean {
    return status === GoodsReceiptStatus.Processing || status === GoodsReceiptStatus.Completed;
  }

  override downloadReport(documentId: string): Observable<Blob> {
    return this.goodsReceiptService.downloadReport(documentId);
  }
}
